import type {
  BookingDelivery,
  CalendarCapability,
  CalendarProvider,
  ConferenceProvider,
  IntegrationView,
  IntegrationsView,
} from "@/api/types";

/**
 * One place that knows what calendar providers exist, what to call them, and which video
 * provider each one brings.
 *
 * Previously every surface asked `provider === "GOOGLE"` and each carried its own copy of the
 * provider-to-label switch, so adding a provider meant finding all of them. Anything
 * provider-shaped belongs here.
 *
 * The helpers read a LIST of connections rather than one, even though a host holds a single
 * calendar today. That is what lets a surface ask "can this host mint a Teams link" without
 * knowing how many calendars the answer had to consider.
 */

// The wire contract owns the set; this module owns what each one is called on screen.
export type CalendarProviderId = CalendarProvider;

export type CalendarProviderMeta = {
  id: CalendarProviderId;
  /** What the host calls it, not what we call it internally. */
  label: string;
  /** The bare product name, for places too tight for the full label (a calendar block's sublabel). */
  short: string;
  /** The video provider this calendar can mint links for. */
  video: ConferenceProvider;
};

/** Display order on the profile page. */
export const CALENDAR_PROVIDERS: readonly CalendarProviderMeta[] = [
  { id: "GOOGLE", label: "Google Calendar", short: "Google", video: "GOOGLE_MEET" },
  { id: "MICROSOFT", label: "Outlook Calendar", short: "Outlook", video: "MICROSOFT_TEAMS" },
] as const;

export function calendarProvider(id: string): CalendarProviderMeta | undefined {
  return CALENDAR_PROVIDERS.find((p) => p.id === id);
}

/**
 * What to call the calendar something came from. `short` for tight spaces (a calendar block's
 * sublabel), the full label everywhere else.
 *
 * <p>An unknown or missing provider falls back to "your calendar" rather than guessing. Guessing is
 * exactly what the meetings calendar did while Google was the only source: it said "from Google"
 * for every imported event, and went on saying it once Outlook could be connected.</p>
 */
export function calendarProviderName(id: string | undefined | null, form: "short" | "full" = "full"): string {
  const meta = id ? calendarProvider(id) : undefined;
  if (!meta) return "your calendar";
  return form === "short" ? meta.short : meta.label;
}

/** Label for a video provider, wherever one is shown. */
export function conferenceProviderLabel(provider: string | null | undefined): string {
  switch (provider) {
    case "GOOGLE_MEET":
      return "Google Meet";
    case "MICROSOFT_TEAMS":
      return "Microsoft Teams";
    case "ZOOM":
      return "Zoom";
    default:
      return "Video call";
  }
}

/**
 * The providers this deployment can connect. Falls back to the pre-`available` shape so a
 * browser holding a cached bundle still works while the backend rolls out.
 */
export function availableProviders(iv: IntegrationsView | null): CalendarProviderId[] {
  if (!iv) return [];
  if (iv.available) {
    return iv.available.filter((id): id is CalendarProviderId => !!calendarProvider(id));
  }
  return iv.googleAvailable ? ["GOOGLE"] : [];
}

/** Every calendar the host holds, whatever its status. The primary comes first. */
export function connections(iv: IntegrationsView | null): IntegrationView[] {
  return iv?.connections ?? [];
}

/** The host's connection for one provider, whatever its status. */
export function connectionFor(
  iv: IntegrationsView | null,
  id: CalendarProviderId,
): IntegrationView | null {
  return connections(iv).find((c) => c.provider === id) ?? null;
}

/**
 * What each calendar permission is for, in the host's terms rather than the provider's.
 *
 * Ordered as shown on the card, availability first: it is the one whose absence is invisible
 * everywhere else, because a calendar that cannot see free/busy fails by quietly offering
 * invitees time the host has already committed.
 */
export const CALENDAR_CAPABILITIES: readonly {
  id: CalendarCapability;
  label: string;
}[] = [
  { id: "AVAILABILITY", label: "Availability (free/busy)" },
  { id: "EVENTS", label: "Add events to your calendar" },
] as const;

/**
 * The permissions a connection is missing.
 *
 * Absent on the wire (an older backend, or a cached bundle) reads as "nothing missing", the same
 * as an empty list: this is a warning surface, and one it cannot substantiate must not appear.
 */
export function missingCapabilities(
  connection: IntegrationView | null,
): CalendarCapability[] {
  return connection?.missingCapabilities ?? [];
}

/**
 * Whether a connection is short of a permission it needs.
 *
 * Deliberately independent of `status`. The pair is the whole point: ERROR means the token died
 * and reconnecting fixes it, while this means the token is fine and the consent was incomplete.
 * A host who sees only "Connected" for the second case has no way to find out.
 */
export function hasIncompleteConsent(connection: IntegrationView | null): boolean {
  return missingCapabilities(connection).length > 0;
}

/** The providers whose calendar is connected and working. */
export function activeProviders(iv: IntegrationsView | null): CalendarProviderId[] {
  return connections(iv)
    .filter((c) => c.status === "ACTIVE")
    .map((c) => calendarProvider(c.provider)?.id)
    .filter((id): id is CalendarProviderId => !!id);
}

/**
 * Whether the host can mint links for a video provider right now: the calendar that offers it
 * must be connected and working. A PERSONAL Microsoft account is excluded even though its
 * calendar syncs, because Graph cannot create a Teams meeting for one.
 */
export function canMint(iv: IntegrationsView | null, video: ConferenceProvider): boolean {
  return videoState(iv, video) === "ready";
}

/**
 * What a host can do about one video provider, in one value.
 *
 * This replaced a pair of booleans that could not tell "you have no calendar" apart from "the
 * calendar you have cannot mint this", so a host with a connected calendar was shown the same
 * "Connect a calendar" prompt against both options and could select neither.
 */
export type VideoState =
  /** An active calendar mints this. The only state that is selectable. */
  | "ready"
  /** The calendar that mints it needs reconnecting. */
  | "reconnect"
  /** Offered here, and the host holds no calendar at all. */
  | "connect"
  /** Offered here, but the host's one calendar slot is taken by the other provider. */
  | "switch"
  /** Connected and syncing, but this account cannot host the meeting (personal Microsoft). */
  | "account"
  /** Not configured in this deployment. */
  | "unavailable"
  /** Declared, but nothing mints it yet. */
  | "soon";

export function videoState(
  iv: IntegrationsView | null,
  video: ConferenceProvider,
): VideoState {
  const meta = CALENDAR_PROVIDERS.find((p) => p.video === video);
  if (!meta) return "soon"; // Zoom and anything else declared but unbuilt
  if (!availableProviders(iv).includes(meta.id)) return "unavailable";

  const held = connectionFor(iv, meta.id);
  if (held) {
    if (held.status !== "ACTIVE") return "reconnect";
    // The calendar syncs; Graph just will not create a Teams meeting for a consumer account.
    if (meta.id === "MICROSOFT" && held.accountType === "PERSONAL") return "account";
    return "ready";
  }
  // Nothing connected for this provider. Whether the host can simply add it depends on whether
  // their calendar slot is free.
  return connections(iv).length === 0 ? "connect" : "switch";
}

/** One video option for the meeting-location picker. */
export type VideoOption = {
  value: ConferenceProvider;
  state: VideoState;
};

/**
 * The video options to offer a host, in display order.
 *
 * Derived from what they have connected rather than listed per builder, so the two builders
 * cannot drift and a provider gained or lost needs no change in either.
 */
export function videoOptions(iv: IntegrationsView | null): VideoOption[] {
  const options: VideoOption[] = CALENDAR_PROVIDERS.map((p) => ({
    value: p.video,
    state: videoState(iv, p.video),
  }));
  // Declared in the API and on the pricing page, but nothing mints it yet.
  options.push({ value: "ZOOM", state: "soon" });
  return options;
}

/**
 * What the "when a meeting is auto-booked" choice should render, in one value.
 *
 * <p>`locked` means there is no calendar to write to, so "Add to my calendar" must not be offered
 * as a selectable option at all: the profile shows a connect action in its place and email is what
 * actually happens. `active` is the route a meeting would really take, which is not always the
 * stored preference — the default is `CALENDAR` and the backend falls back to email whenever no
 * usable calendar is connected (`CalendarSyncService`), so showing the raw preference would state
 * the opposite of the truth.</p>
 *
 * <p>The stored preference is deliberately left alone while locked rather than being rewritten to
 * `EMAIL`. It is the host's intent, and keeping it means connecting a calendar later resumes
 * calendar delivery on its own instead of silently leaving them on email.</p>
 *
 * <p>`loaded` false means the connection state is not known yet, and neither does `readOnly` (the
 * admin user page renders this form against a profile whose integrations are not the ones the API
 * would return). Both fall back to displaying the stored preference and lock nothing, because
 * asserting "you have no calendar" while still finding out is the failure this type exists to
 * avoid — the same reason `videoState` treats `null` as loading rather than unavailable.</p>
 */
export function deliveryState(
  iv: IntegrationsView | null,
  opts: { loaded: boolean; readOnly: boolean; stored: BookingDelivery },
): { locked: boolean; active: BookingDelivery } {
  if (opts.readOnly || !opts.loaded) {
    return { locked: false, active: opts.stored };
  }
  if (activeProviders(iv).length === 0) {
    return { locked: true, active: "EMAIL" };
  }
  return { locked: false, active: opts.stored };
}

/** Message for an `?error=` code coming back from any provider's OAuth callback. */
export function describeCallbackError(code: string): string {
  const [providerId, ...rest] = code.split("_");
  const name = calendarProvider(providerId.toUpperCase())?.label ?? "the calendar";
  switch (rest.join("_")) {
    case "denied":
      return `${name} connection cancelled. Nothing was linked.`;
    case "state":
      return `The ${name} sign-in link expired. Try connecting again.`;
    case "already_connected":
      return "Disconnect your other calendar first, then connect this one.";
    case "exchange":
      return `${name} didn't complete the connection. Try again.`;
    default:
      return `Couldn't connect ${name}. Try again.`;
  }
}
